/**
 * Series (formerly "Journey") config.
 *
 * Data from the Journey prototype (2026-07): sessions that entered the single
 * opted-in Journey collection read 5.30 discoveries on average vs. 0.09 for
 * everyone else - a 59x lift. That was strong enough evidence to universalize
 * the mechanic: every collection now behaves as a Series, so every reader
 * gets Episode X of Y, Previous/Next, progress, and Season Complete.
 *
 * What lives here now:
 *   - CURATED_NEXT: for the top series, a hand-picked "watch next"
 *     recommendation shown on Season Complete. Better than the generic
 *     "Browse Collections" fallback.
 *   - getJourney(): returns a Series config for ANY known collection slug,
 *     so the discovery/collection pages always render Series UI and the
 *     `from=` search param is always propagated on shared discoveries.
 *
 * Series title/description come from the collection record itself; we only
 * add curated eyebrows and next-recommendations here.
 */

export type JourneyConfig = {
  /** Eyebrow above the collection title. Defaults to "Series". */
  eyebrow: string;
  /** Recommended next series shown on Season Complete. Null → generic CTA. */
  next: {
    slug: string;
    title: string;
    /** One-line "why this next" pitch. */
    pitch: string;
  } | null;
};

/**
 * Hand-curated next-series recommendations. Only the top ~half of collections
 * get one - the rest fall back to "Browse Series" so we don't force weak
 * pairings. Order the pairings by thematic adjacency, not size.
 */
const CURATED: Record<string, JourneyConfig> = {
  "venom-and-poison": {
    eyebrow: "Series · Nature's Deadliest Chemistry",
    next: {
      slug: "places-on-earth-that-can-kill-you",
      title: "Places On Earth That Can Kill You",
      pitch: "You survived the venom. Now try the geography.",
    },
  },
  "places-on-earth-that-can-kill-you": {
    eyebrow: "Series · Deadly Geography",
    next: {
      slug: "things-that-can-kill-you",
      title: "Things That Can Kill You",
      pitch: "The places nearly killed you. Now the objects.",
    },
  },
  "things-that-can-kill-you": {
    eyebrow: "Series · Everyday Danger",
    next: {
      slug: "apex-predators",
      title: "Apex Predators",
      pitch: "You know how it kills you. Meet what kills you.",
    },
  },
  "apex-predators": {
    eyebrow: "Series · Top Of The Food Chain",
    next: {
      slug: "the-tiny-and-terrifying",
      title: "The Tiny And Terrifying",
      pitch: "The biggest predators are done. Meet the smallest ones.",
    },
  },
  "the-tiny-and-terrifying": {
    eyebrow: "Series · Small Things That Wreck You",
    next: {
      slug: "microscopic-world",
      title: "The Microscopic World",
      pitch: "You saw the tiny. Now go microscopic.",
    },
  },
  "microscopic-world": {
    eyebrow: "Series · Life Under The Lens",
    next: {
      slug: "blood-and-guts",
      title: "Blood And Guts",
      pitch: "Zoom back out. Straight into your own body.",
    },
  },
  "ocean-deep": {
    eyebrow: "Series · Descent Into The Deep",
    next: {
      slug: "animals-that-shouldnt-exist",
      title: "Animals That Shouldn't Exist",
      pitch: "You met the abyss. Now meet what shouldn't exist.",
    },
  },
  "animals-that-shouldnt-exist": {
    eyebrow: "Series · Evolution's Weird Ideas",
    next: {
      slug: "natures-biggest-liars",
      title: "Nature's Biggest Liars",
      pitch: "Weird bodies, weirder deceptions.",
    },
  },
  "natures-biggest-liars": {
    eyebrow: "Series · Evolutionary Deception",
    next: {
      slug: "nature-solved-it-first",
      title: "Nature Solved It First",
      pitch: "You saw nature lie. Now watch it invent.",
    },
  },
  "space-is-trying-to-kill-you": {
    eyebrow: "Series · The Hostile Universe",
    next: {
      slug: "cosmic-mysteries",
      title: "Cosmic Mysteries",
      pitch: "Space wants you dead. Also, no one knows how it works.",
    },
  },
  "cosmic-mysteries": {
    eyebrow: "Series · The Universe We Can't Explain",
    next: {
      slug: "worlds-stranger-than-science-fiction",
      title: "Worlds Stranger Than Science Fiction",
      pitch: "Cosmic mysteries next door.",
    },
  },
  "the-human-brain-files": {
    eyebrow: "Series · Inside The Brain",
    next: {
      slug: "senses-redefined",
      title: "Senses Redefined",
      pitch: "The brain decides what's real. Now watch it decide differently.",
    },
  },
  "senses-redefined": {
    eyebrow: "Series · Your Senses Are Lying",
    next: {
      slug: "human-body-glitches",
      title: "Human Body Glitches",
      pitch: "Now that senses can't be trusted - meet the bugs in the hardware.",
    },
  },
  "inside-your-body": {
    eyebrow: "Series · The Body Tour",
    next: {
      slug: "blood-and-guts",
      title: "Blood And Guts",
      pitch: "Deeper into the machine.",
    },
  },
  "blood-and-guts": {
    eyebrow: "Series · The Wet Machinery",
    next: {
      slug: "human-body-glitches",
      title: "Human Body Glitches",
      pitch: "Now watch the machinery misfire.",
    },
  },
  "human-body-glitches": {
    eyebrow: "Series · When The Body Betrays You",
    next: {
      slug: "sleep-dreams-and-the-night",
      title: "Sleep, Dreams And The Night",
      pitch: "The glitches get weirder at night.",
    },
  },
  "mind-bending-physics": {
    eyebrow: "Series · Reality, But Wrong",
    next: {
      slug: "things-we-still-cant-explain",
      title: "Things We Still Can't Explain",
      pitch: "Physics gets weird. Then it gives up.",
    },
  },
  "things-we-still-cant-explain": {
    eyebrow: "Series · The Unsolved",
    next: {
      slug: "worlds-stranger-than-science-fiction",
      title: "Worlds Stranger Than Science Fiction",
      pitch: "Some answers are stranger than the questions.",
    },
  },
  "plants-strike-back": {
    eyebrow: "Series · Plants Are Not Innocent",
    next: {
      slug: "venom-and-poison",
      title: "Venom And Poison",
      pitch: "You underestimated plants. Now animals.",
    },
  },
  "survival-superpowers": {
    eyebrow: "Series · Impossible Ways To Stay Alive",
    next: {
      slug: "frozen-worlds",
      title: "Frozen Worlds",
      pitch: "Try surviving here.",
    },
  },
  "frozen-worlds": {
    eyebrow: "Series · Life In The Cold",
    next: {
      slug: "extremes-on-earth",
      title: "The Most Extreme Places On Earth",
      pitch: "Cold was just the start.",
    },
  },
  "extremes-on-earth": {
    eyebrow: "Series · Earth At Its Limit",
    next: {
      slug: "weather-gone-wrong",
      title: "Weather Gone Wrong",
      pitch: "Extreme places. Now extreme skies.",
    },
  },
  "ancient-earth-files": {
    eyebrow: "Series · Deep Time",
    next: {
      slug: "the-edge-of-extinction",
      title: "The Edge Of Extinction",
      pitch: "Ancient extinctions. Modern ones next.",
    },
  },
};

const DEFAULT_SERIES: JourneyConfig = { eyebrow: "Series", next: null };

export function isJourney(slug: string | null | undefined): boolean {
  // Every collection is now a Series.
  return !!slug;
}

/**
 * Returns a Series config for ANY collection slug. Curated collections get
 * a themed eyebrow + hand-picked next recommendation; everything else gets
 * a generic "Series" eyebrow with a null next (renders a "Browse Series"
 * fallback CTA on completion).
 */
/** Collections retired in the Aug 2026 cleanup - never recommend them. */
const RETIRED = new Set([
  "places-on-earth-that-can-kill-you", "microscopic-world", "natures-biggest-liars",
  "human-body-glitches", "sleep-dreams-and-the-night", "mind-bending-physics",
  "things-we-still-cant-explain", "the-edge-of-extinction", "the-rarest-animals-on-earth",
  "rocks-and-minerals", "hidden-rivers-and-lakes", "food-secrets", "animal-architects",
  "chemistry-of-everything", "going-viral-in-science",
]);

export function getJourney(slug: string | null | undefined): JourneyConfig | null {
  if (!slug) return null;
  const cfg = CURATED[slug] ?? DEFAULT_SERIES;
  if (cfg.next && RETIRED.has(cfg.next.slug)) return { ...cfg, next: null };
  return cfg;
}

